import { useEffect, useState, useCallback } from 'react';
import { collection, query, where, getDocs, Timestamp } from 'firebase/firestore';
import { db } from '../../../../../Config/firebase';
import { motion } from 'framer-motion';
import { Tooltip } from '@mui/material';
import { Link } from 'react-router-dom';
import { useAppSelector } from '../../../../../state/state';
import { selectEffectiveUID } from '../../../../../state/UserStatesV2/userSliceV2';


interface TodayStats {
    paidOrders: number;
    paidAmount: number;
    settledOrders: number;
    settledAmount: number;
}

export default function TodaysSummary() {
    const [isLoading, setIsLoading] = useState(true);
    const [stats, setStats] = useState<TodayStats>({
        paidOrders: 0,
        paidAmount: 0,
        settledOrders: 0,
        settledAmount: 0
    });
    const ServiceProviderId = useAppSelector(selectEffectiveUID);
   

    const loadTodayStats = useCallback(async () => {
        if (!ServiceProviderId) {
            setIsLoading(false);
            return;
        }
        
        try {
            setIsLoading(true);
            const ordersRef = collection(db, 'ServiceProviders', ServiceProviderId, 'Orders');

            // Get today's start and current timestamps
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            const now = new Date();

            // Convert to Firestore Timestamps
            const todayTimestamp = Timestamp.fromDate(today);
            const nowTimestamp = Timestamp.fromDate(now);

            // Query for today's paid orders
            const paidOrdersQuery = query(
                ordersRef,
                where('paymentTime', '>=', todayTimestamp),
                where('paymentTime', '<=', nowTimestamp),
                where('payment_status', '==', 'Paid')
            );

            // Query for today's settled orders
            const settledOrdersQuery = query(
                ordersRef,
                where('settlementTime', '>=', todayTimestamp),
                where('settlementTime', '<=', nowTimestamp),
                where('settlement_status', '==', 'Settled')
            );

            const [paidOrdersSnapshot, settledOrdersSnapshot] = await Promise.all([
                getDocs(paidOrdersQuery),
                getDocs(settledOrdersQuery)
            ]);

            setStats({
                paidOrders: paidOrdersSnapshot.size,
                paidAmount: paidOrdersSnapshot.docs.reduce((sum, doc) => sum + (doc.data().chargeable || 0), 0),
                settledOrders: settledOrdersSnapshot.size,
                settledAmount: settledOrdersSnapshot.docs.reduce((sum, doc) => sum + (doc.data().transferAmount || 0), 0)
            });

            setIsLoading(false);
        } catch (error) {
            console.error('Error loading today\'s statistics:', error);
            setIsLoading(false);
        }
    }, [ServiceProviderId]);

    useEffect(() => {
        if (ServiceProviderId) {
            loadTodayStats();
        }
    }, [ServiceProviderId, loadTodayStats]);

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-24">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-gray-900"></div>
            </div>
        );
    }

    return (
        <div className="bg-white rounded-xl shadow-sm p-4 mb-4">
            <div className="flex items-center justify-between mb-4">
                <div>
                    <h2 className="text-lg font-semibold text-gray-900">Today's Summary</h2>
                    <p className="text-sm text-gray-500">Quick overview of today's transactions</p>
                </div>
                <Link 
                    to="/statistics" 
                    className="text-sm text-cc_brand hover:text-cc_brand_hover font-medium"
                >
                    View Details →
                </Link>
            </div>

            <div className="grid grid-cols-2 gap-4">
                {/* Today's Payments Card */}
                <Tooltip title="Orders paid today" arrow placement="top">
                    <div className="bg-gradient-to-br from-purple-50 to-purple-100 p-4 rounded-lg border border-purple-200">
                        <div className="flex items-start justify-between">
                            <div>
                                <p className="text-sm font-medium text-gray-600">Payments</p>
                                <p className="text-xl font-bold text-purple-600 mt-1">
                                    ₹{stats.paidAmount.toFixed(2)}
                                </p>
                                <p className="text-xs text-gray-500 mt-1">{stats.paidOrders} orders</p>
                            </div>
                            <motion.div
                                className="p-2 bg-purple-600 bg-opacity-10 rounded-full"
                                animate={{
                                    scale: [1, 1.1, 1],
                                }}
                                transition={{
                                    duration: 2,
                                    repeat: Infinity,
                                }}
                            >
                                <svg className="w-4 h-4 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                            </motion.div>
                        </div>
                    </div>
                </Tooltip>

                {/* Placeholder for Today's Settlements Card */}
                <Tooltip title="Orders settled today" arrow placement="top">
                    <div className="bg-gradient-to-br from-indigo-50 to-indigo-100 p-4 rounded-lg border border-indigo-200">
                        <div className="flex items-start justify-between">
                            <div>
                                <p className="text-sm font-medium text-gray-600">Settlements</p>
                                <p className="text-xl font-bold text-indigo-600 mt-1">
                                    ₹{stats.settledAmount.toFixed(2)}
                                </p>
                                <p className="text-xs text-gray-500 mt-1">{stats.settledOrders} orders</p>
                            </div>
                            <motion.div
                                className="p-2 bg-indigo-600 bg-opacity-10 rounded-full"
                                animate={{
                                    scale: [1, 1.1, 1],
                                }}
                                transition={{
                                    duration: 2,
                                    repeat: Infinity,
                                }}
                            >
                                <svg className="w-4 h-4 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                            </motion.div>
                        </div>
                    </div>
                </Tooltip>
            </div>
        </div>
    );
}